import { useEffect, useState } from "react";
import { AuthLayout } from "../layouts/AuthLayout";
import { getApiBase } from "../../api/auth";
import "./LoginPage.css";

const Q1_OPTS = ["Très pertinente", "Pertinente", "Moyennement pertinente", "Peu pertinente"] as const;
const Q2_OPTS = ["Très claire et applicable", "Assez claire", "Partiellement claire", "Peu claire"] as const;
const Q3_OPTS = [
  "La vision de l'agriculture de précision",
  "Le modèle de réseau d'ambassadeurs",
  "La plateforme et ses fonctionnalités",
  "Les programmes de formation continue",
  "Le modèle d'affaires / d'intégration des services",
] as const;
const Q5_OPTS = ["Très probable", "Possible", "Peu probable", "Pas applicable à mon contexte"] as const;
const Q6_OPTS = ["Oui, à court terme", "Oui, éventuellement", "Pas pour le moment"] as const;
const Q7_OPTS = [
  "Une démonstration plus technique de la plateforme",
  "Une discussion sur le modèle de collaboration / ambassadeur",
  "Un échange sur un projet ou un besoin spécifique",
  "Recevoir de l'information supplémentaire",
] as const;

function getTokenFromHash(): string {
  const h = typeof window !== "undefined" ? window.location.hash : "";
  const idx = h.indexOf("?");
  if (idx === -1) return "";
  return new URLSearchParams(h.slice(idx + 1)).get("t") ?? "";
}

export function PresentationXlkeySurveyPage() {
  const [token] = useState(() => getTokenFromHash());
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<{
    valid: boolean;
    already_submitted?: boolean;
    broadcast?: boolean;
  } | null>(null);
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState<string[]>([]);
  const [q3Other, setQ3Other] = useState("");
  const [q5, setQ5] = useState("");
  const [q6, setQ6] = useState("");
  const [q7, setQ7] = useState<string[]>([]);
  const [q7Other, setQ7Other] = useState("");
  const [q8, setQ8] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setStatus({ valid: false });
      return;
    }
    let cancelled = false;
    fetch(`${getApiBase()}/public/survey-presentation-xlkey?t=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        if (!cancelled) setStatus({ valid: false });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const toggleMulti = (list: string[], setList: (v: string[]) => void, opt: string) => {
    if (list.includes(opt)) setList(list.filter((x) => x !== opt));
    else setList([...list, opt]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || status?.already_submitted) return;
    setMessage(null);
    setSubmitting(true);
    let q3Send = [...q3];
    if (q3Other.trim() && !q3Send.includes("Autre")) q3Send.push("Autre");
    let q7Send = [...q7];
    if (q7Other.trim() && !q7Send.includes("Autre")) q7Send.push("Autre");

    const answers: Record<string, unknown> = {};
    if (q1) answers.q1 = q1;
    if (q2) answers.q2 = q2;
    if (q3Send.length) {
      answers.q3 = q3Send.map((x) => (x === "Autre" ? "Autre" : x));
      if (q3Other.trim()) answers.q3_other = q3Other.trim();
    }
    if (q5) answers.q5 = q5;
    if (q6) answers.q6 = q6;
    if (q7Send.length) {
      answers.q7 = q7Send.map((x) => (x === "Autre" ? "Autre" : x));
      if (q7Other.trim()) answers.q7_other = q7Other.trim();
    }
    if (q8.trim()) answers.q8 = q8.trim();

    try {
      const res = await fetch(`${getApiBase()}/public/survey-presentation-xlkey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, answers }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setMessage({ type: "err", text: "Ce sondage a déjà été complété." });
        setStatus((s) => (s ? { ...s, already_submitted: true } : s));
        return;
      }
      if (!res.ok) {
        setMessage({
          type: "err",
          text: typeof data.detail === "string" ? data.detail : "Impossible d'enregistrer les réponses.",
        });
        return;
      }
      const isBroadcast = data.broadcast === true;
      if (isBroadcast) {
        setMessage({
          type: "ok",
          text: "Merci ! Les réponses sont enregistrées. Le même lien peut être utilisé par une autre personne pour répondre à son tour.",
        });
        setQ1("");
        setQ2("");
        setQ3([]);
        setQ3Other("");
        setQ5("");
        setQ6("");
        setQ7([]);
        setQ7Other("");
        setQ8("");
        setStatus({ valid: true, already_submitted: false, broadcast: true });
      } else {
        setMessage({ type: "ok", text: "Merci ! Vos réponses ont bien été enregistrées." });
        setStatus({ valid: true, already_submitted: true });
      }
    } catch {
      setMessage({ type: "err", text: "Erreur réseau. Réessayez plus tard." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout className="auth-layout--compact">
        <div className="login-page p-6">
          <p className="text-sm text-muted">Chargement…</p>
        </div>
      </AuthLayout>
    );
  }

  if (!token || status?.valid === false) {
    return (
      <AuthLayout className="auth-layout--compact">
        <div className="login-page">
          <div className="login-header">
            <div className="login-brand">XLKey</div>
            <h1>Lien invalide</h1>
            <p>Ce lien de sondage n’est pas valide ou a expiré. Utilisez le lien reçu dans votre courriel.</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (status?.already_submitted) {
    return (
      <AuthLayout className="auth-layout--compact">
        <div className="login-page">
          <div className="login-header">
            <div className="login-brand">XLKey</div>
            <h1>Merci</h1>
            <p>Vous avez déjà répondu à ce sondage.</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout className="auth-layout--compact">
      <div className="login-page login-page--survey mx-auto w-full">
        <div className="login-header">
          <div className="login-brand">XLKey</div>
          <h1>Sondage d’appréciation</h1>
          <p className="text-sm text-muted">
            {status?.broadcast
              ? "Lien partageable : chaque personne peut envoyer une réponse (environ 2 min). Vos réponses sont traitées de façon confidentielle."
              : "Présentation XLKey, environ 2 minutes. Vos réponses sont confidentielles."}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="login-form space-y-6 text-left">
          <section>
            <p className="text-sm font-medium text-text mb-2">1) Globalement, comment avez-vous évalué la présentation ?</p>
            <div className="space-y-1">
              {Q1_OPTS.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="q1" checked={q1 === o} onChange={() => setQ1(o)} />
                  {o}
                </label>
              ))}
            </div>
          </section>
          <section>
            <p className="text-sm font-medium text-text mb-2">
              2) Dans quelle mesure la vision présentée vous semble-t-elle claire et applicable dans votre contexte ?
            </p>
            <div className="space-y-1">
              {Q2_OPTS.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="q2" checked={q2 === o} onChange={() => setQ2(o)} />
                  {o}
                </label>
              ))}
            </div>
          </section>
          <section>
            <p className="text-sm font-medium text-text mb-2">3) Parmi les éléments présentés, lesquels vous ont le plus intéressé ? (choix multiples)</p>
            <div className="space-y-1">
              {Q3_OPTS.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={q3.includes(o)}
                    onChange={() => toggleMulti(q3, setQ3, o)}
                  />
                  {o}
                </label>
              ))}
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={q3.includes("Autre")}
                  onChange={() => toggleMulti(q3, setQ3, "Autre")}
                  className="mt-1"
                />
                <span className="flex-1">
                  Autre :
                  <input
                    type="text"
                    className="login-input mt-1 w-full"
                    value={q3Other}
                    onChange={(e) => setQ3Other(e.target.value)}
                    placeholder="Précisez"
                  />
                </span>
              </label>
            </div>
          </section>
          <section>
            <p className="text-sm font-medium text-text mb-2">
              5) À quel point voyez-vous une possibilité d’intégrer ce type d’outils ou d’approche dans votre pratique ou votre organisation ?
            </p>
            <div className="space-y-1">
              {Q5_OPTS.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="q5" checked={q5 === o} onChange={() => setQ5(o)} />
                  {o}
                </label>
              ))}
            </div>
          </section>
          <section>
            <p className="text-sm font-medium text-text mb-2">
              6) Seriez-vous intéressé à poursuivre la discussion ou à en apprendre davantage ?
            </p>
            <div className="space-y-1">
              {Q6_OPTS.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="q6" checked={q6 === o} onChange={() => setQ6(o)} />
                  {o}
                </label>
              ))}
            </div>
          </section>
          <section>
            <p className="text-sm font-medium text-text mb-2">7) Si oui, quel type de suivi serait le plus pertinent pour vous ? (choix multiples)</p>
            <div className="space-y-1">
              {Q7_OPTS.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={q7.includes(o)}
                    onChange={() => toggleMulti(q7, setQ7, o)}
                  />
                  {o}
                </label>
              ))}
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={q7.includes("Autre")}
                  onChange={() => toggleMulti(q7, setQ7, "Autre")}
                  className="mt-1"
                />
                <span className="flex-1">
                  Autre :
                  <input
                    type="text"
                    className="login-input mt-1 w-full"
                    value={q7Other}
                    onChange={(e) => setQ7Other(e.target.value)}
                    placeholder="Précisez"
                  />
                </span>
              </label>
            </div>
          </section>
          <section>
            <label htmlFor="q8" className="text-sm font-medium text-text block mb-2">
              8) Avez-vous des commentaires ou suggestions pour améliorer notre approche ou nos outils ?
            </label>
            <textarea
              id="q8"
              className="login-input w-full min-h-[100px]"
              value={q8}
              onChange={(e) => setQ8(e.target.value)}
              rows={4}
            />
          </section>

          {message && (
            <div
              className="login-message text-sm"
              style={{
                color: message.type === "ok" ? "var(--color-success, #059669)" : "var(--color-danger, #dc2626)",
              }}
            >
              {message.text}
            </div>
          )}

          <button type="submit" className="login-submit" disabled={submitting}>
            {submitting ? "Envoi…" : "Envoyer mes réponses"}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}
